import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { getUserToken } from '@/redux/user';
import {
    useGetPromotionalBannersQuery,
    useDeletePromotionalBannerMutation,
    useDuplicatePromotionalBannerMutation,
} from '@/services/innovorder/promotionalBanner/promotionalBanner.endpoints';
import type { PromotionalBanner, LinkType } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';
import { useGetMenusQuery } from '@/services/innovorder/menu/menu.endpoints';
import { useGetCategoriesQuery } from '@/services/innovorder/categories/categories.endpoints';
import { useGetProductsQuery } from '@/services/innovorder/products/products.endpoints';
import { useGetPickupSchedulesQuery } from '@/services/innovorder/restaurant/restaurant.endpoints';
import { useGetRestaurantChannelsQuery } from '@/services/innovorder/channel/channel.endpoints';
import { ChannelId } from '@/services/innovorder/channel/channel.type';

import type { TableRow } from '@/components/atoms/Table/Table.type';

import { useBannerAction } from './hooks/useBannerAction';
import { useBannerToggle } from './hooks/useBannerToggle';
import { buildPromotionalBannerRows } from './utils/buildPromotionalBannerRows';
import { getPromotionalBannerHeaders } from './utils/getPromotionalBannerHeaders';
import { convertTableSortToQuerySort } from './utils/convertTableSortToQuerySort';

const DEFAULT_SORT = [{ id: 'status', desc: false }];

export const usePromotionalBannersVM = () => {
    const intl = useIntl();
    const token = useSelector(getUserToken) as string;
    const match = useRouteMatch<{ brandId: string; restaurantId: string }>();
    const brandId = Number(match.params.brandId);
    const restaurantId = Number(match.params.restaurantId);

    const sortQuery = useMemo(() => convertTableSortToQuerySort(DEFAULT_SORT), []);

    const { data: banners = [], isLoading } = useGetPromotionalBannersQuery(
        { token, restaurantId, sort: sortQuery },
        { skip: !restaurantId },
    );

    const [deleteBanner] = useDeletePromotionalBannerMutation();
    const [duplicateBanner] = useDuplicatePromotionalBannerMutation();

    const [fallbackState, setFallbackState] = useState<
        { isOpen: false } | { isOpen: true; sourceName: string; droppedLinkType: LinkType }
    >({ isOpen: false });

    const { data: menus = [] } = useGetMenusQuery({ restaurantId }, { skip: !restaurantId });
    const { data: schedulesData } = useGetPickupSchedulesQuery(
        { token, restaurantId: String(restaurantId) },
        { skip: !restaurantId },
    );
    const schedules = schedulesData?.pickupSchedules ?? [];

    const { data: channels } = useGetRestaurantChannelsQuery(
        { restaurantId: String(restaurantId), token },
        { skip: !restaurantId },
    );
    const hasKioskChannel = useMemo(
        () => !!channels && Object.values(channels).some((ch) => ch.channelId === ChannelId.KIOSK),
        [channels],
    );

    const hasActiveKioskMenu = useMemo(
        () => menus.some((m) => m.isActive && m.channels.some((c) => c.channelId === ChannelId.KIOSK)),
        [menus],
    );

    const kioskMenuId = useMemo(() => {
        const kioskMenu = menus.find((m) => m.isActive && m.channels.some((c) => c.channelId === ChannelId.KIOSK));
        return kioskMenu?.menuId;
    }, [menus]);

    const { data: categories = [] } = useGetCategoriesQuery({ menuId: kioskMenuId! }, { skip: !kioskMenuId });
    const { data: products = [] } = useGetProductsQuery({ menuId: kioskMenuId! }, { skip: !kioskMenuId });

    const { action, clearAction, openCreateModal, openEditModal, openViewModal, openDeleteModal } = useBannerAction();

    const { handleToggle, confirmReloadCallback, handleConfirmReload, handleCancelReload } = useBannerToggle({
        token,
        restaurantId,
        schedules,
    });

    const handleDelete = useCallback(
        async (banner: PromotionalBanner) => {
            await deleteBanner({
                token,
                promotionalBannerId: banner.promotionalBannerId,
            });
            clearAction();
        },
        [token, deleteBanner, clearAction],
    );

    const handleDuplicate = useCallback(
        async (banner: PromotionalBanner) => {
            const created = await duplicateBanner({
                token,
                promotionalBannerId: banner.promotionalBannerId,
                restaurantId,
            }).unwrap();

            const srcHadLink = banner.linkType !== 'none';
            const copyFellBack = created.linkType === 'none';

            if (srcHadLink && copyFellBack) {
                setFallbackState({
                    isOpen: true,
                    sourceName: banner.name,
                    droppedLinkType: banner.linkType as 'product' | 'category',
                });
            }
        },
        [duplicateBanner, token, restaurantId],
    );

    const handleDismissFallback = useCallback(() => setFallbackState({ isOpen: false }), []);

    const headers = useMemo(() => getPromotionalBannerHeaders(intl), [intl]);

    const rows = useMemo(
        () =>
            buildPromotionalBannerRows({
                banners,
                intl,
                products,
                categories,
                onToggle: handleToggle,
                onEdit: openEditModal,
                onView: openViewModal,
                onDelete: openDeleteModal,
                onDuplicate: handleDuplicate,
            }),
        [
            banners,
            intl,
            products,
            categories,
            handleToggle,
            openEditModal,
            openViewModal,
            openDeleteModal,
            handleDuplicate,
        ],
    );

    const customRowProps = useCallback(
        (row: TableRow) => {
            const bannerId = row.id?.value;
            const banner = banners.find((b) => b.promotionalBannerId === bannerId);
            if (banner?.isActive) {
                return { style: { backgroundColor: '#F2FDF5' } };
            }
            return {};
        },
        [banners],
    );

    const handleRowClick = useCallback(
        (cell?: { row: { original: TableRow } }) => {
            if (!cell) return;
            const bannerId = cell.row.original.id?.value;
            const banner = banners.find((b) => b.promotionalBannerId === bannerId);
            if (!banner) return;
            if (banner.brandId === null) {
                openEditModal(banner);
            } else {
                openViewModal(banner);
            }
        },
        [banners, openEditModal, openViewModal],
    );

    return {
        banners,
        headers,
        rows,
        isLoading,
        hasKioskChannel,
        hasActiveKioskMenu,
        action,
        brandId,
        restaurantId,
        token,
        schedules,
        clearAction,
        openCreateModal,
        customRowProps,
        handleRowClick,
        handleToggle,
        handleDelete,
        handleDuplicate,
        confirmReloadCallback,
        handleConfirmReload,
        handleCancelReload,
        fallbackState,
        handleDismissFallback,
    };
};
